// src/pages/Login.js
import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, setTokens } from "../api/client";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const [emailOrUser, setEmailOrUser] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false); // ✅ added
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // IMPORTANT: auth:false so we don’t send a stale Bearer
  async function doLogin(payload) {
    // ✅ Django endpoints expect trailing slash; avoid redirect -> GET -> 405
    return api.post("/api/auth/login/", payload, { auth: false, credentialsMode: "omit" });
  }

  function extractTokens(data) {
    const access = data?.access_token ?? data?.access;
    const refresh = data?.refresh_token ?? data?.refresh;
    return { access, refresh };
  }

  function isEducator(role) {
    return (role || "").toUpperCase() === "EDUCATOR";
  }

  function isScholarOnlyPath(pathname) {
    // Keep this list small and explicit. We'll tighten this up further in routes.js.
    if (!pathname) return false;
    const scholarOnly = new Set([
      "/my-points",
    ]);
    // If you later add scholar routes like /scholar/... you can enable this:
    // if (pathname.startsWith("/scholar")) return true;
    return scholarOnly.has(pathname);
  }

  function isEducatorOnlyPath(pathname) {
    if (!pathname) return false;
    const educatorOnly = new Set([
      "/dashboard",
    ]);
    // If you later add educator routes like /educator/... you can enable this:
    // if (pathname.startsWith("/educator")) return true;
    return educatorOnly.has(pathname);
  }

  function defaultPathForRole(role) {
    return isEducator(role) ? "/dashboard" : "/my-points";
  }

  // Make sure we never redirect back to a public page after login,
  // and also prevent redirecting into the wrong role's area.
  function computePostLoginPath(role) {
    const publicPaths = new Set([
      "/",
      "/welcome",
      "/login",
      "/signup",
      "/recover",
      "/recover/educator",
      "/recover/scholar",
    ]);

    const fromPath = location.state?.from?.pathname || "";

    // If we have a prior "from" path and it isn't public, we *might* honor it,
    // BUT only if it matches the user's role.
    if (fromPath && !publicPaths.has(fromPath)) {
      // Role mismatch protection:
      if (isEducator(role) && isScholarOnlyPath(fromPath)) {
        return "/dashboard";
      }
      if (!isEducator(role) && isEducatorOnlyPath(fromPath)) {
        return "/my-points";
      }

      return fromPath;
    }

    // Otherwise, role-based default
    return defaultPathForRole(role);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;

    setErr("");
    setBusy(true);

    try {
      const ident = (emailOrUser || "").trim();
      if (!ident || !pw) {
        setErr("Please enter your email/username and password.");
        return;
      }

      // 1) Try email-first
      let login = await doLogin({ email: ident, password: pw });

      // 2) If 401/400, try username
      if (!login.ok && (login.status === 401 || login.status === 400)) {
        login = await doLogin({ username: ident, password: pw });
      }

      // Debug line to help if we get stuck on login screen
      // eslint-disable-next-line no-console
      console.debug("[login] status:", login?.status, "ok:", login?.ok);

      if (!login.ok) {
        const d = login.data;
        const detail = typeof d === "string" ? d : (d?.detail || d?.message);
        if (login.status === 401) setErr(detail || "Invalid email/username or password.");
        else if (login.status === 403) setErr(detail || "Account not active.");
        else if (login.status === 404) setErr("Login endpoint not found. Check vercel.json rewrite / API base.");
        else setErr(detail || `Login failed (${login.status}).`);
        // keep email for convenience; clear only the password
        setPw("");
        return;
      }

      // 3) Store tokens (supports either token shape)
      const { access, refresh } = extractTokens(login.data);
      if (!access || !refresh) {
        setErr("Login succeeded but tokens were missing from the response.");
        setPw("");
        return;
      }
      setTokens({ access, refresh });

      // 4) Now verify identity
      // ✅ trailing slash here too
      const me = await api.get("/api/auth/me/");
      if (!me.ok) {
        const d = me.data;
        setErr(typeof d === "string" ? d : (d?.detail || "Could not load your profile."));
        setPw("");
        return;
      }

      const role = (me?.data?.role || "").toUpperCase();
      const target = computePostLoginPath(role);

      // ✅ navigate away on success
      navigate(target, { replace: true });
      return;
    } catch {
      setErr("Network/CORS error. Confirm API base and backend CORS.");
      setPw("");
      return;
    } finally {
      // always stop the spinner (even on success; navigate will unmount the component next tick)
      setBusy(false);
    }
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <h1 style={styles.h1}>Sign In</h1>

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Email or Username"
            value={emailOrUser}
            onChange={(e) => setEmailOrUser(e.target.value)}
            style={styles.input}
            autoComplete="username"
            required
            disabled={busy}
          />

          {/* Password with eye toggle */}
          <div style={{ position: "relative" }}>
            <input
              type={showPw ? "text" : "password"}
              placeholder="Password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              style={{ ...styles.input, paddingRight: 36 }}
              autoComplete="current-password"
              required
              disabled={busy}
            />
            <button
              type="button"
              aria-label={showPw ? "Hide password" : "Show password"}
              onClick={() => setShowPw((s) => !s)}
              style={styles.eyeBtn}
            >
              {showPw ? "🙈" : "👁️"}
            </button>
          </div>

          <button type="submit" disabled={busy} style={styles.btn}>
            {busy ? "Signing in..." : "Sign In"}
          </button>

          {err && <div style={styles.error}>{err}</div>}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 14 }}>
            <Link to="/recover" style={styles.a}>Forgot password?</Link>
            <Link to="/signup" style={styles.a}>Create account</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  wrap: { minHeight: "100vh", display: "grid", placeItems: "center", background: "#F7F9FC", padding: 24 },
  card: { width: 380, maxWidth: "92vw", background: "#fff", 
    padding: 24, borderRadius: 12, boxShadow: "0 10px 24px rgba(0,0,0,0.08)", display: "grid", gap: 12 },
  h1: { margin: 0, fontSize: 22, color: "#003e73", textAlign: "center" },
  input: { height: 44, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 12, padding: "0 12px" },
  btn: { height: 44, border: "none", borderRadius: 999, 
    background: "#f9a21a", color: "#fff", fontWeight: 800, cursor: "pointer" },
  error: { color: "#b00020", background: "#FFEAEA", 
    border: "1px solid #F5B5B5", borderRadius: 8, padding: "8px 10px" },
  a: { color: "#003e73", textDecoration: "underline" },
  // ✅ style for the eye button
  eyeBtn: { position: "absolute", right: 8, top: 8, height: 28, width: 28, 
    border: "1px solid rgba(0,0,0,0.2)", borderRadius: 6, background: "#fff", cursor: "pointer" },
};
